import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "../../../Components/Buttons";
import { FormImage, FormInput, FormSection, FormSelect, FormSwitch } from "../../../Components/Form";
import { ToastMessage } from "../../../Components/Utils";
import BreadCrumb from "../../../Components/BreadCrumb";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { banner_image_url } from "../../../Components/ImageUrls";
import { handleError } from "../../../stores/components/handleError";
import {
  useCreateBannerMutation,
  useUpdateBannerMutation,
} from "../../../stores/api/banners.api";
import { buildFormData } from "../../../stores/api/helpers/payload.helper";

const TYPE_OPTIONS = [
  { value: "hero_banner", label: "Hero Banner" },
  { value: "slide", label: "Slide" },
  { value: "event", label: "Event" },
];

const POSITION_OPTIONS = [
  { value: "left_bottom",   label: "Left Bottom" },
  { value: "left_top",      label: "Left Top" },
  { value: "left",          label: "Left Center" },
  { value: "right_bottom",  label: "Right Bottom" },
  { value: "right_top",     label: "Right Top" },
  { value: "right",         label: "Right Center" },
  { value: "center_bottom", label: "Center Bottom" },
  { value: "center_top",    label: "Center Top" },
  { value: "center",        label: "Center" },
];

const EMPTY = {
  title: "",
  badge: "",
  link: "",
  short_description: "",
  button_text: "",
  button_color: "#ff6600",
  image: null,
  image_alt: "",
  status: false,
  type: "hero_banner",
  data_position: "left_bottom",
  start_date: "",
  end_date: "",
};

const CreateBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const details = location?.state?.banner;

  const [createBanner, { isLoading: creating }] = useCreateBannerMutation();
  const [updateBanner, { isLoading: updating }] = useUpdateBannerMutation();
  const loading = creating || updating;

  const [formData, setFormData] = useState({
    ...EMPTY,
    ...(details
      ? {
          title: details.title || "",
          badge: details.badge || "",
          link: details.link || "",
          short_description: details.short_description || "",
          button_text: details.button_text || "",
          button_color: details.button_color || "#ff6600",
          image_alt: details.image_alt || "",
          status: details.status ?? false,
          type: details.type || "hero_banner",
          data_position: details.data_position || "left_bottom",
          start_date: details.start_date ? details.start_date.slice(0, 10) : "",
          end_date: details.end_date ? details.end_date.slice(0, 10) : "",
        }
      : {}),
  });

  const setField = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const resetForm = () => setFormData(EMPTY);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const body = buildFormData({
        ...formData,
        status: formData.status ? 1 : 0,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
      });

      if (details) {
        await updateBanner({ id: details.id, body }).unwrap();
      } else {
        await createBanner(body).unwrap();
      }

      ToastMessage({ type: "success", title: "Success", message: details ? "Banner updated!" : "Banner created!" });
      if (!details) resetForm();
    } catch (err) {
      handleError(err, "Something went wrong");
    }
  };

  return (
    <div className="p-4">
      <BreadCrumb title={details ? "Edit Banner" : "Create Banner"} />

      <form onSubmit={onSubmit} className="mx-auto mt-6 dark:bg-slate-900 rounded-lg p-6 space-y-5">
        <FormSection title="Banner Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Title" name="title" value={formData.title} required onChange={(val) => setField("title", val)} placeholder="Enter banner title" />
            <FormInput label="Badge (optional)" name="badge" value={formData.badge} onChange={(val) => setField("badge", val)} placeholder="e.g. Premium Picks, New Arrival" />
            <FormInput label="Link URL" name="link" value={formData.link} onChange={(val) => setFormData((prev) => ({ ...prev, link: val }))} placeholder="https://example.com" />
            <FormInput label="Short Description" name="short_description" value={formData.short_description} onChange={(val) => setFormData((prev) => ({ ...prev, short_description: val }))} placeholder="Brief description" rows={2} />
            <FormInput label="Button Text" name="button_text" value={formData.button_text} onChange={(val) => setFormData((prev) => ({ ...prev, button_text: val }))} placeholder="e.g. Shop Now" />
            <div>
              <label className="block font-medium mb-1 text-gray-700 dark:text-gray-200">Button Color</label>
              <div className="flex items-center gap-3">
                <input type="color" value={formData.button_color} onChange={(e) => setFormData((prev) => ({ ...prev, button_color: e.target.value }))} className="h-10 w-16 rounded cursor-pointer border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800" />
                <span className="text-sm text-gray-500 dark:text-gray-400">{formData.button_color}</span>
              </div>
            </div>
            <FormSelect label="Type" required options={TYPE_OPTIONS} value={formData.type} onChange={(val) => setFormData((prev) => ({ ...prev, type: val ?? "hero_banner" }))} />
            <FormSelect label="Content Position" options={POSITION_OPTIONS} value={formData.data_position} onChange={(val) => setField("data_position", val ?? "left_bottom")} />
          </div>
        </FormSection>

        {formData.type === "event" && <FormSection title="Schedule (optional)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Start Date" name="start_date" type="date" value={formData.start_date} onChange={(val) => setFormData((prev) => ({ ...prev, start_date: val }))} />
            <FormInput label="End Date" name="end_date" type="date" value={formData.end_date} onChange={(val) => setFormData((prev) => ({ ...prev, end_date: val }))} />
          </div>
        </FormSection>}

        <FormSection title="Banner Image">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormImage label="Image" required initialPreviewUrl={details?.image ? (banner_image_url ?? "") + details.image : null} onChange={(file) => setFormData((prev) => ({ ...prev, image: file }))} />
            <FormInput label="Image Alt Text" name="image_alt" value={formData.image_alt} required onChange={(val) => setField("image_alt", val)} placeholder="Describe the image" />
          </div>
        </FormSection>

        <FormSection title="Status">
          <FormSwitch label="Status" checked={!!formData.status} onChange={(val) => setFormData((prev) => ({ ...prev, status: val }))} />
        </FormSection>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button type="button" disabled={loading} onClick={() => { resetForm(); navigate(ROUTE_PATHS.BANNER); }} className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200">
            Cancel
          </Button>
          <Button type="submit" loading={loading} disabled={loading} className="!bg-green-500 !text-white hover:!bg-green-600" loadingText={details ? "Updating..." : "Creating..."}>
            {details ? "Update Banner" : "Create Banner"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateBanner;
